import type { PurchaseOrderStatus } from './purchaseTypes';

/** Atajos del listado (no son estados de la OC). Labels: compras-f3-cierre.md paso 4. */
export type PurchaseQueueId = 'to_receive' | 'to_pay' | 'overdue';
export type PurchaseQueueFilter = 'all' | PurchaseQueueId;

export const PURCHASE_QUEUE_LABELS: Record<PurchaseQueueId, string> = {
  to_receive: 'Por recibir',
  to_pay: 'Por pagar',
  overdue: 'Vencidas',
};

export const PURCHASE_QUEUE_IDS: PurchaseQueueId[] = ['to_receive', 'to_pay', 'overdue'];

export type PurchaseQueueExpense = {
  purchaseOrderId: string;
  paymentStatus: 'unpaid' | 'partial' | 'paid';
  dueDate: string | null;
  /** Opcionales: permiten mostrar el saldo pendiente en la fila (proto compras). */
  amount?: number;
  amountPaid?: number;
  invoiceDocType?: number | null;
};

export type PurchaseQueueCounts = {
  toReceive: number;
  toPay: number;
  overdue: number;
};

/** Por recibir: enviada o parcial. No draft / received / closed / cancelled. */
export function isToReceiveStatus(status: PurchaseOrderStatus): boolean {
  return status === 'sent' || status === 'partial';
}

/** Por pagar: gasto ligado unpaid o partial (no paid). */
export function isToPayExpense(expense: PurchaseQueueExpense): boolean {
  return expense.paymentStatus === 'unpaid' || expense.paymentStatus === 'partial';
}

/** Vencidas: gasto unpaid/partial con due_date anterior a hoy. */
export function isOverdueExpense(expense: PurchaseQueueExpense, todayIso: string): boolean {
  return isToPayExpense(expense) && expense.dueDate != null && expense.dueDate < todayIso;
}

export function countPurchaseQueues(params: {
  orderStatuses: PurchaseOrderStatus[];
  expenses: PurchaseQueueExpense[];
  todayIso: string;
}): PurchaseQueueCounts {
  return {
    toReceive: params.orderStatuses.filter(isToReceiveStatus).length,
    toPay: params.expenses.filter(isToPayExpense).length,
    overdue: params.expenses.filter((expense) => isOverdueExpense(expense, params.todayIso)).length,
  };
}

export function purchaseOrderIdsForQueue(
  queue: PurchaseQueueId,
  expenses: PurchaseQueueExpense[],
  todayIso: string,
): string[] {
  if (queue === 'to_receive') return [];
  const match =
    queue === 'to_pay'
      ? isToPayExpense
      : (expense: PurchaseQueueExpense) => isOverdueExpense(expense, todayIso);
  return [...new Set(expenses.filter(match).map((expense) => expense.purchaseOrderId))];
}

export type PurchaseRowPaymentPill = {
  label: string;
  tone: 'warn' | 'bad';
  /** Saldo pendiente sumado de los gastos ligados sin terminar de pagar. */
  pendingAmount: number;
};

/**
 * Pill de pago para la fila de lista: 'Vencida' pisa 'Por pagar'.
 * null si no hay gasto ligado o está todo pago.
 */
export function purchaseRowPaymentPill(
  expenses: PurchaseQueueExpense[],
  todayIso: string,
): PurchaseRowPaymentPill | null {
  if (!expenses.length) return null;
  const pendingAmount = expenses
    .filter(isToPayExpense)
    .reduce((sum, expense) => sum + Math.max(0, (expense.amount ?? 0) - (expense.amountPaid ?? 0)), 0);
  if (expenses.some((expense) => isOverdueExpense(expense, todayIso))) {
    return { label: 'Vencida', tone: 'bad', pendingAmount };
  }
  if (expenses.some(isToPayExpense)) {
    return { label: 'Por pagar', tone: 'warn', pendingAmount };
  }
  return null;
}

export function formatPurchaseQueueChipLabel(queue: PurchaseQueueId, count: number): string {
  return `${PURCHASE_QUEUE_LABELS[queue]} ${count}`;
}

/** RI: algún gasto linked activo sin `invoice_doc_type`. */
export function purchaseOrderMissingFiscal(
  expenses: PurchaseQueueExpense[],
  fiscalCaptureEnabled: boolean,
): boolean {
  if (!fiscalCaptureEnabled) return false;
  return expenses.some((e) => e.invoiceDocType == null);
}
